import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'gtlc-mobile-staff-badge-session';

/**
 * Persists the server-signed badge-session token returned by POST /badges/verify
 * (or /badges/msal-verify). The token is a short-lived HMAC-signed JWT
 * (5-minute lifetime) that carries the badge-verified staff identity forward to
 * subsequent tablet API calls as the X-Staff-Session-Token header.
 *
 * Security: the token is only ever obtained from the backend (never constructed
 * client-side) and expires in 5 minutes, so a stale stored value is rejected
 * server-side (401) rather than granting access. localStorage survives PWA
 * reloads and matches the app's existing storage pattern (DeviceTokenService,
 * MSAL cache).
 */
@Injectable({ providedIn: 'root' })
export class BadgeSessionService {
  readonly #token = signal<string | null>(this.readFromStorage());
  readonly token = this.#token.asReadonly();

  hasToken(): boolean {
    return this.#token() !== null;
  }

  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEY, token);
    this.#token.set(token);
  }

  clearToken(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.#token.set(null);
  }

  private readFromStorage(): string | null {
    return localStorage.getItem(STORAGE_KEY);
  }
}
